/* eslint-disable max-lines */
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageHeader } from '@utils/hooks/use-page-header';
import { useTranslation } from 'react-i18next';
import { formatDate, formatDuration } from '@utils/date';
import { useGetModels } from '@utils/api/models/use-get-models';
import Button from '@components/atoms/ui-kit/button.tsx';
import { useGetSttSessions } from '@utils/api/sessions/use-get-stt-sessions';
import { useGetSessionProgress } from '@utils/api/sessions/use-get-session-progress';
import {
  ProgressBar,
  rendersProgressBar
} from '@components/atoms/ui-kit/progress-bar';
import { useOrganization } from '@utils/api/users/use-get-organization';
import toast from 'react-hot-toast';
import { AxiosError } from 'axios';
import { BaseError } from '@utils/types/shared/base';
import { SttSession } from '@utils/types/stt-sessions/stt-sessions';
import { capitalizeFirstLetter } from '@utils/capitalize';
import { useDeleteStt } from '@utils/api/sessions/use-delete-stt';
import ConfirmationModal from '@components/atoms/modals/confirmation-modal.tsx';
import { ExternalLink, RefreshCw, Share2, Trash2 } from 'lucide-react';
import { Tooltip } from '@components/atoms/ui-kit/tooltip';
import {
  getErrorCategoryLabel,
  getErrorCategoryStyle
} from '@utils/types/errors/error-categories';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import { Badge } from '@components/atoms/ui-kit/badge.tsx';
import { Text } from '@components/atoms/ui-kit/text.tsx';
import { Axios } from '@utils/api/axios-instance';
import { GET_URL, POST_URL } from '@utils/api/endpoints';
import { PATHS } from '../../router/paths';
import { SharedLinkResult } from '@utils/types/editors/magic-link';
import { useRetryAdminSession } from '@utils/api/admin/use-retry-admin-session';

import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import { GridAction } from '@components/atoms/datatable/grid-action-controller';
import { useGetMe } from '@utils/api/users/use-get-me.tsx';

type SttSessionRow = SttSession & {
  duration_display: string;
  date_session_start_display: string;
  last_update_display: string;
  status_display: string;
  language_display: string;
  customer_name?: string;
  error?: string | null;
  error_category?: string | null;
};

type SessionDoc = { id: string; type: string };

async function fetchSessionDocs(sessionId: string): Promise<SessionDoc[]> {
  const { data } = await Axios.get(GET_URL(sessionId).sessionDocuments);
  return (data?.result ?? []) as SessionDoc[];
}

export default function SttSessions() {
  const { t } = useTranslation();
  const { getColour, getBrandTint } = useThemeContext();
  const navigate = useNavigate();

  const { data, isLoading, refetch, isFetching } = useGetSttSessions();
  const { data: progressMap } = useGetSessionProgress();
  const { data: supportedLanguages } = useGetModels();
  const { data: organization } = useOrganization();
  const deleteStt = useDeleteStt();
  const retrySession = useRetryAdminSession();
  const { data: me } = useGetMe();

  const userRoles = (me?.result?.roles ?? []) as unknown as string[];
  const isAdmin = userRoles.includes('admin') || userRoles.includes('sysop');

  const [retryingItems, setRetryingItems] = useState(false);
  const [selectedItem, setSelectedItem] = useState<SttSessionRow | null>(null);

  // Delete state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Share state
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [isGeneratingShare, setIsGeneratingShare] = useState(false);

  // Re-run state
  const [isRerunModalOpen, setIsRerunModalOpen] = useState(false);

  const handleRefresh = async () => {
    setRetryingItems(true);
    try {
      await refetch();
      toast.success(t('refreshed'));
    } catch (error) {
      toast.error(t('an-error-has-occurred'));
    } finally {
      setRetryingItems(false);
    }
  };

  const statusRenderer = useCallback(
    (params: { value: string; data: SttSessionRow }) => {
      const raw = (params.value || '').toLowerCase();
      const label = raw.charAt(0).toUpperCase() + raw.slice(1);
      const session = params.data;
      const progressItem = progressMap?.get(session.session_id) ?? null;
      const inFlight = raw === 'processing' || raw === 'queued';

      const isBadFile = raw === 'error' && session?.error_code === 'bad_file';
      const badge =
        raw === 'processing' ? (
          <span
            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
            style={{
              backgroundColor: getBrandTint(0.12),
              color: getColour('activeText'),
              boxShadow: `0 0 0 1px ${getBrandTint(0.25)}`
            }}
          >
            {label}
          </span>
        ) : (
          <Badge
            color={
              raw === 'finished' ? 'green' : raw === 'error' ? 'red' : 'zinc'
            }
          >
            {isBadFile ? 'BAD FILE' : label}
          </Badge>
        );

      let content: React.ReactNode;
      if (raw === 'error' && session?.error) {
        const categoryStyle = getErrorCategoryStyle(session.error_category);
        content = isBadFile ? (
          <Tooltip
            label={
              <div className="max-w-xs text-left">
                {session.error_reason && (
                  <Text
                    as="span"
                    size="xs"
                    weight="semibold"
                    className="block mb-1"
                    style={{ color: getColour('danger') }}
                  >
                    {session.error_reason.replace(/_/g, ' ')}
                  </Text>
                )}
                <Text as="span" size="xs">
                  {session.error}
                </Text>
              </div>
            }
          >
            {badge}
          </Tooltip>
        ) : (
          <Tooltip
            label={
              <div className="max-w-xs text-left">
                {session.error_category && (
                  <Text
                    as="span"
                    size="xs"
                    weight="semibold"
                    className="block uppercase mb-1"
                    style={{ color: categoryStyle.color }}
                  >
                    {getErrorCategoryLabel(session.error_category)}
                  </Text>
                )}
                <Text as="span" size="xs">
                  {session.error}
                </Text>
              </div>
            }
          >
            {badge}
          </Tooltip>
        );
      } else {
        content = badge;
      }

      // The bar replaces the status text rather than sitting under it: while a
      // session is working the percentage is the more useful of the two, and
      // "Processing" above a moving bar is saying the same thing twice. The
      // label survives as the bar's tooltip and accessible name.
      //
      // rendersProgressBar guards the swap. Replacing the label with a bar that
      // then declines to draw would leave the cell blank, so anything the bar
      // will not render keeps the chip.
      if (
        inFlight &&
        progressItem &&
        rendersProgressBar(progressItem.progress_state)
      ) {
        return (
          <ProgressBar
            pct={progressItem.progress_pct}
            state={progressItem.progress_state}
            label={label}
          />
        );
      }

      return content;
    },
    [getBrandTint, getColour, progressMap]
  );

  const normalizeLanguageInput = useMemo(() => {
    const list = supportedLanguages?.result ?? [];
    return (val: string) => {
      const q = (val || '').trim().toLowerCase();
      if (!q) return undefined;
      if (/^[a-z]{2}(-[a-z]{2})?$/.test(q)) return q;
      const hit = list.find(
        (l: { key: string; name: string }) =>
          l.name?.toLowerCase().includes(q) || l.key?.toLowerCase().includes(q)
      );
      return hit?.key || undefined;
    };
  }, [supportedLanguages]);

  // ─── Row action handlers ────────────────────────────────────────────────────

  const handleOpenSession = async (row: SttSessionRow) => {
    const toastId = toast.loading(t('opening-session'));
    try {
      const docs = await fetchSessionDocs(row.session_id);
      const doc =
        docs.find(d => d.type?.toLowerCase() === 'transcript') ?? docs[0];
      if (doc) {
        toast.dismiss(toastId);
        navigate(PATHS(row.session_id, doc.id).TRANSCRIPT);
      } else {
        toast.error(t('no-documents-found'), { id: toastId });
      }
    } catch {
      toast.error(t('an-error-has-occurred'), { id: toastId });
    }
  };

  const handleShareSession = async (row: SttSessionRow) => {
    setSelectedItem(row);
    setShareUrl(null);
    setIsGeneratingShare(true);
    setIsShareModalOpen(true);
    try {
      const docs = await fetchSessionDocs(row.session_id);
      const doc =
        docs.find(d => d.type?.toLowerCase() === 'transcript') ?? docs[0];
      if (!doc) {
        toast.error(t('no-documents-found'));
        setIsShareModalOpen(false);
        return;
      }
      const { data: linkData } = await Axios.post<SharedLinkResult>(
        POST_URL(row.session_id, doc.id).share
      );
      setShareUrl(linkData?.result?.link ?? null);
    } catch {
      toast.error(t('an-error-has-occurred'));
      setIsShareModalOpen(false);
    } finally {
      setIsGeneratingShare(false);
    }
  };

  const handleRerunSession = (sessionId: string) => {
    if (!sessionId) return;
    const toastId = toast.loading(t('retrying-session'));
    retrySession.mutate(sessionId, {
      onSuccess: () => {
        toast.success(t('session-requeued-successfully'), { id: toastId });
        setIsRerunModalOpen(false);
      },
      onError: () => {
        toast.error(t('an-error-has-occurred'), { id: toastId });
        setIsRerunModalOpen(false);
      }
    });
  };

  const handleDeleteSession = (id: string) => {
    if (!id) return;
    const toastId = toast.loading(t('deleting-session'));
    deleteStt.mutate(id, {
      onSuccess: () => {
        toast.success(t('session-deleted-successfully'), { id: toastId });
      },
      onError: (error: unknown) => {
        const fallbackMessage = t('an-error-has-occurred');
        const errData = (error as AxiosError<BaseError>)?.response?.data;
        let messages: string[] = [];
        if (Array.isArray(errData?.detail)) {
          type ValidationErr = { loc?: string[]; msg?: string };
          messages = (errData.detail as ValidationErr[]).map(err => {
            const field = err.loc?.[1] ?? 'field';
            return `${field}: ${err.msg}`;
          });
        } else if (errData?.message) {
          messages = [errData.message];
        } else if (errData?.error_description) {
          messages = [errData.error_description];
        } else {
          messages = [fallbackMessage];
        }
        toast.error(messages.join('\n'), { id: toastId });
      },
      onSettled: () => {
        setIsDeleteModalOpen(false);
      }
    });
  };

  // ─── Columns ────────────────────────────────────────────────────────────────

  const columns = useMemo<DataGridColumn<SttSessionRow>[]>(
    () => [
      {
        id: 'session_id',
        field: 'session_id',
        accessorKey: 'id',
        header: t('session-id'),
        filterVariant: 'text',
        filterKey: 'session_id',
        serverOperator: 'eq',
        enableSorting: true,
        enableRowCopyOnClick: false
      },
      {
        id: 'filename',
        field: 'filename',
        accessorKey: 'filename',
        header: t('filename'),
        minWidth: 400,
        filterVariant: 'text',
        filterKey: 'filename',
        serverOperator: 'ilike',
        enableSorting: true,
        clampLines: 2
      },
      {
        id: 'status',
        field: 'status',
        accessorKey: 'status_display',
        header: t('status'),
        maxWidth: 120,
        cell: ({ row }) =>
          statusRenderer({
            value: row.original.status_display,
            data: row.original
          }),
        filterVariant: 'select',
        filterKey: 'status',
        serverOperator: 'eq',
        filterOptions: [
          { label: capitalizeFirstLetter('queued'), value: 'queued' },
          { label: capitalizeFirstLetter('processing'), value: 'processing' },
          { label: capitalizeFirstLetter('finished'), value: 'finished' },
          { label: capitalizeFirstLetter('error'), value: 'error' }
        ],
        enableSorting: true
      },
      {
        id: 'language',
        field: 'language',
        accessorKey: 'language_display',
        header: t('language'),
        maxWidth: 160,
        filterVariant: 'text',
        filterKey: 'language',
        serverOperator: 'eq',
        normalizeFilterValue: normalizeLanguageInput,
        enableSorting: true
      },
      {
        id: 'date_from',
        accessorKey: 'date_session_start_display',
        header: t('date-from'),
        maxWidth: 180,
        filterVariant: 'date',
        filterKey: 'date_from',
        serverOperator: 'gte',
        enableSorting: false,
        enableFiltering: true
      },
      {
        id: 'date_to',
        accessorKey: 'last_update_display',
        header: t('date-to'),
        maxWidth: 180,
        filterVariant: 'date',
        filterKey: 'date_to',
        serverOperator: 'lte',
        enableSorting: false,
        enableFiltering: true
      },
      {
        id: 'duration_display',
        field: 'duration_display',
        accessorKey: 'duration_display',
        header: t('duration'),
        maxWidth: 120,
        enableFiltering: false,
        enableSorting: false
      },
      {
        id: 'date_session_start',
        field: 'date_session_start',
        accessorKey: 'date_session_start_display',
        header: t('date-session-start'),
        maxWidth: 220,
        enableFiltering: false,
        enableSorting: true
      },
      {
        id: 'last_update',
        field: 'last_update',
        accessorKey: 'last_update_display',
        header: t('last-update'),
        maxWidth: 220,
        enableFiltering: false,
        enableSorting: true
      }
    ],
    [t, normalizeLanguageInput, statusRenderer]
  );

  // ─── Rows ───────────────────────────────────────────────────────────────────

  const rows: SttSessionRow[] = useMemo(() => {
    if (!data) return [];
    return data.result.map(item => {
      const languageKey = item.language?.split('-')[0]?.toLowerCase();
      const languageData = supportedLanguages?.result.find(
        (lang: { key: string; name: string }) =>
          lang.key?.toLowerCase() === languageKey
      );
      return {
        ...item,
        customer_name: organization?.name || '-',
        status_display: capitalizeFirstLetter(item.status),
        duration_display: formatDuration(Number(item.duration)),
        language_display: languageData?.name ?? item.language ?? '-',
        date_session_start_display: formatDate(item.date_session_start || ''),
        last_update_display: formatDate(item.last_update || '')
      };
    });
  }, [data, supportedLanguages, organization]);

  // ─── Grid actions (inline row buttons) ──────────────────────────────────────

  const actions = useMemo<GridAction<SttSessionRow>[]>(
    () => [
      {
        id: 'open',
        label: t('open'),
        icon: <ExternalLink className="h-3.5 w-3.5" />,
        run: row => handleOpenSession(row)
      },
      {
        id: 'share',
        label: t('share'),
        icon: <Share2 className="h-3.5 w-3.5" />,
        run: row => handleShareSession(row)
      },
      ...(isAdmin
        ? [
            {
              id: 'rerun',
              label: t('re-run'),
              icon: <RefreshCw className="h-3.5 w-3.5" />,
              run: (row: SttSessionRow) => {
                setSelectedItem(row);
                setIsRerunModalOpen(true);
              }
            },
            {
              id: 'delete',
              label: t('delete'),
              intent: 'danger' as const,
              icon: <Trash2 className="h-3.5 w-3.5" />,
              run: (row: SttSessionRow) => {
                setSelectedItem(row);
                setIsDeleteModalOpen(true);
              }
            }
          ]
        : [])
    ],
    [t, isAdmin]
  );

  usePageHeader({
    title: t('speech-to-text-sessions-history'),
    description: t(
      'speech-to-text-sessions-history-files-with-id-status-and-other-details'
    ),
    actions: (
      <Button
        onClick={handleRefresh}
        icon={<RefreshCw width={14} />}
        label={t('refresh-sessions')}
        disabled={retryingItems}
      />
    )
  });

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="bg-app min-h-screen">
      <div className="pb-4 sm:pb-0">
        <DataGridV2<SttSessionRow>
          data={rows}
          columns={columns}
          actions={actions}
          manualPagination
          manualSorting
          manualFiltering
          totalItems={data ? data.total_results : undefined}
          enablePagination
          enableSorting
          enableFiltering
          enableRowSelection={false}
          showColumnFilters={false}
          filterMode="popover"
          height="88vh"
          dense
          striped
          grid={false}
          loading={isLoading || isFetching || !data}
          getRowId={row => String(row.id)}
        />
      </div>

      {/* Delete confirmation */}
      {isDeleteModalOpen && selectedItem && (
        <ConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={() => handleDeleteSession(String(selectedItem.id))}
          size="small"
          title={t('delete-session-confirmation')}
          confirmLabel={t('delete')}
          caption={t('are-you-sure-you-want-to-delete-this-session')}
          cancelLabel={t('cancel')}
          confirmDisabled={deleteStt.isLoading}
          destructive
          autoFocusConfirm
        >
          {['queued', 'processing'].includes(
            (selectedItem.status ?? '').toLowerCase()
          ) && (
            <div
              className="mt-2 rounded-lg px-3 py-2 text-sm"
              style={{
                backgroundColor: 'rgba(245,158,11,0.1)',
                color: 'rgb(161,98,7)',
                border: '1px solid rgba(245,158,11,0.3)'
              }}
            >
              {t('delete-session-cancel-processing-warning', {
                status: selectedItem.status_display
              })}
            </div>
          )}
        </ConfirmationModal>
      )}

      {/* Re-run confirmation */}
      {isRerunModalOpen && selectedItem && (
        <ConfirmationModal
          isOpen={isRerunModalOpen}
          onClose={() => setIsRerunModalOpen(false)}
          onConfirm={() => handleRerunSession(selectedItem.session_id)}
          size="small"
          title={t('re-run-session')}
          confirmLabel={t('re-run')}
          caption={t('are-you-sure-you-want-to-rerun-this-session')}
          cancelLabel={t('cancel')}
          confirmDisabled={retrySession.isLoading}
          destructive={false}
          autoFocusConfirm
        />
      )}

      {/* Share modal */}
      {isShareModalOpen && selectedItem && (
        <ConfirmationModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          onConfirm={() => {
            if (shareUrl) {
              navigator.clipboard.writeText(shareUrl);
              toast.success(t('copied-to-clipboard'));
            }
            setIsShareModalOpen(false);
          }}
          size="medium"
          title={t('share-session')}
          confirmLabel={shareUrl ? t('copy-link') : '...'}
          cancelLabel={t('close')}
          confirmDisabled={isGeneratingShare || !shareUrl}
          destructive={false}
        >
          {isGeneratingShare ? (
            <div className="flex items-center justify-center py-6">
              <div
                className="h-5 w-5 animate-spin rounded-full border-2 border-t-transparent"
                style={{ borderColor: getColour('activeText') }}
              />
            </div>
          ) : shareUrl ? (
            <div className="flex flex-col gap-3 py-1">
              <Text as="p" size="sm" tone="muted">
                {t('share-link-ready')}
              </Text>
              <div className="rounded-xl border border-hairline bg-app-soft px-3 py-2 break-all select-all">
                <Text as="span" size="xs" className="font-mono">
                  {shareUrl}
                </Text>
              </div>
            </div>
          ) : null}
        </ConfirmationModal>
      )}
    </div>
  );
}
